import { Clock, Loader2, Stethoscope, Users } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useInstantConsultation } from 'hooks/useInstantConsultation'

export function QueueStep() {
  const { session, handleLeaveQueue, loading } = useInstantConsultation()
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [leaving, setLeaving] = useState(false)

  const onLeaveQueue = async () => {
    setLeaving(true)
    try {
      await handleLeaveQueue()
    } finally {
      setLeaving(false)
    }
  }

  // Contador de tempo na fila
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsedSeconds((prev) => prev + 1)
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  const formatElapsed = (seconds: number) => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, '0')
    const s = (seconds % 60).toString().padStart(2, '0')
    return `${m}:${s}`
  }

  return (
    <div className="mx-auto flex w-full max-w-lg flex-col items-center px-4 py-12">
      {/* Animação de ondas */}
      <div className="relative mb-8 flex items-center justify-center">
        <span className="absolute h-32 w-32 animate-ping rounded-full bg-primary-100 opacity-50" />
        <span className="absolute h-24 w-24 animate-ping rounded-full bg-primary-200 opacity-40 [animation-delay:300ms]" />
        <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-primary-700 shadow-lg">
          <Stethoscope className="h-10 w-10 text-white" />
        </div>
      </div>

      <h1 className="font-spartan mb-2 text-center text-2xl font-bold text-gray-800">Aguardando médico</h1>
      <p className="mb-8 text-center text-sm text-gray-500">
        Você está na fila. Um médico especialista em cannabis medicinal vai atender você em instantes.
      </p>

      {/* Cards de informação */}
      <div className="mb-8 grid w-full grid-cols-2 gap-3">
        <div className="flex flex-col items-center gap-2 rounded-2xl bg-white p-4 shadow-sm">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-full"
            style={{ backgroundColor: 'var(--app-green-soft, rgba(33, 151, 110, 0.12))' }}>
            <Clock className="h-5 w-5 text-primary-700" />
          </div>
          <span className="font-mono text-xl font-bold text-gray-800">{formatElapsed(elapsedSeconds)}</span>
          <span className="text-xs text-gray-400">Tempo na fila</span>
        </div>

        <div className="flex flex-col items-center gap-2 rounded-2xl bg-white p-4 shadow-sm">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-full"
            style={{ backgroundColor: 'var(--app-green-soft, rgba(33, 151, 110, 0.12))' }}>
            <Users className="h-5 w-5 text-primary-700" />
          </div>
          <span className="text-xl font-bold text-gray-800">Online</span>
          <span className="text-xs text-gray-400">Médicos disponíveis</span>
        </div>
      </div>

      {/* Informações da sessão */}
      {session && (
        <div className="w-full rounded-2xl bg-white p-4 shadow-sm">
          <h3 className="mb-3 text-sm font-semibold text-gray-600">Detalhes da consulta</h3>
          <div className="flex flex-col gap-2 text-sm text-gray-500">
            <div className="flex items-center justify-between">
              <span>Protocolo</span>
              <span className="font-mono font-medium text-gray-700">#{session.id}</span>
            </div>
            {session.pathologies && session.pathologies.length > 0 && (
              <div className="flex items-start justify-between gap-4">
                <span>Condições</span>
                <span className="text-right font-medium text-gray-700">
                  {session.pathologies.map((p) => p.pathology.name).join(', ')}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      <p className="mt-8 max-w-xs text-center text-xs text-gray-400">
        Por favor, mantenha esta página aberta. Você será direcionado automaticamente para a videochamada assim que um
        médico estiver disponível.
      </p>

      <button
        type="button"
        onClick={onLeaveQueue}
        disabled={leaving || loading}
        className="mt-6 flex items-center justify-center gap-2 rounded-lg border border-solid border-gray-200 bg-white px-6 py-2.5 text-sm font-medium text-gray-500 transition-colors hover:border-red-300 hover:text-red-600 disabled:opacity-60">
        {(leaving || loading) && <Loader2 className="h-4 w-4 animate-spin" />}
        Sair da fila
      </button>
    </div>
  )
}
